import React, { useEffect, useState } from "react";
import emailjs from "emailjs-com";
import ReactWhatsapp from 'react-whatsapp';
import { getFirestore, collection, getDocs, addDoc, serverTimestamp, getDoc, doc } from "firebase/firestore";
import app from "./firebase";
import { useCookies } from "react-cookie";
import { Helmet } from "react-helmet";
import {
  Leaf, Search, Filter, Globe, MessageCircle, Send, X, Clock,
  ChevronRight, Tag, ArrowLeft, Package, User, MapPin, CheckCircle2, AlertTriangle
} from "lucide-react";

const db = getFirestore(app);

const MarketplaceCard = ({ req, onClick }) => (
  <div
    onClick={() => onClick(req)}
    className="card"
    style={{
      padding: "24px", cursor: "pointer", transition: "all 0.3s ease", border: "1px solid var(--color-gray-100)",
      display: "flex", flexDirection: "column", gap: "16px", position: "relative", overflow: "hidden"
    }}
    onMouseEnter={e => {
      e.currentTarget.style.transform = "translateY(-4px)";
      e.currentTarget.style.boxShadow = "var(--shadow-lg)";
      e.currentTarget.style.borderColor = "var(--color-primary-light)";
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "var(--shadow-md)";
      e.currentTarget.style.borderColor = "var(--color-gray-100)";
    }}
  >
    <div style={{ position: "absolute", top: 0, right: 0, padding: "8px 16px", background: "var(--color-primary-light)", color: "var(--color-primary)", fontSize: "11px", fontWeight: 700, borderRadius: "0 0 0 12px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {req.deliveryType}
    </div>

    <div>
      <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "4px" }}>
        {req.product}
      </div>
      <h3 style={{ fontSize: "20px", fontWeight: 800, color: "var(--color-gray-900)", lineHeight: 1.2 }}>
        {req.quantity}
      </h3>
    </div>

    <div style={{ padding: "12px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", fontSize: "13px", color: "var(--color-gray-600)", border: "1px solid var(--color-gray-100)" }}>
      {req.description && req.description.length > 80 ? req.description.substring(0, 80) + "..." : req.description || "No description provided."}
    </div>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
      <div>
        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600 }}>Budget / Price</div>
        <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-primary-dark)" }}>{req.price}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: "11px", color: "var(--color-gray-400)", textTransform: "uppercase", fontWeight: 600 }}>Buyer</div>
        <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-gray-700)" }}>{req.buyer || "Anonymous"}</div>
      </div>
    </div>

    <div style={{ display: "flex", alignItems: "center", gap: "12px", borderTop: "1px solid var(--color-gray-100)", paddingTop: "14px", marginTop: "4px", fontSize: "12px" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--color-gray-400)" }}><Clock size={13} /> {req.time}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--color-gray-400)" }}><Tag size={13} /> {req.paymentMode}</span>
      {req.successfulTransactions !== undefined && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "var(--color-success)", marginLeft: "auto", fontWeight: 600 }}>
          <CheckCircle2 size={13} /> {req.successfulTransactions} transaction{req.successfulTransactions !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  </div>
);

export default function CentralMarket() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ product: "all", payment: "all", delivery: "all" });
  const [mailMessage, setMailMessage] = useState("");
  const [userName, setUserName] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [cookies] = useCookies(["username"]);
  const userId = cookies.username;

  const [proposalForm, setProposalForm] = useState({
    offer: "",
    contact: "",
    notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "buyers_request"));
        let data = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

        // Fetch successful transaction counts for each buyer
        const transactionsSnapshot = await getDocs(collection(db, "market_transactions"));
        const allTransactions = transactionsSnapshot.docs.map(doc => doc.data());

        // Add successful transaction count to each request
        data = data.map(request => {
          const buyerId = request.id.split("_")[0];
          const successfulCount = allTransactions.filter(
            t => t.buyerId === buyerId && (t.status === "completed" || t.status === "delivered")
          ).length;
          return { ...request, successfulTransactions: successfulCount };
        });

        setRequests(data);
        setFilteredRequests(data);

        if (userId) {
          const userDoc = await getDoc(doc(db, "users", userId));
          if (userDoc.exists()) setUserName(userDoc.data().name || "Investor");
        }
      } catch (error) { console.error(error); }
    };
    fetchData();
  }, []);

  const decodeId = (str) => {
    try {
      const decoded = atob(str.replace(/-/g, '+').replace(/_/g, '/'));
      const [id, salt] = decoded.split("|");
      if (salt === "agv_secure_2026") return id;
    } catch (e) { return null; }
    return null;
  };

  // Deep Linking: Auto-open modal if ID is in URL
  useEffect(() => {
    if (requests.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const encryptedId = urlParams.get('id');
      if (encryptedId) {
        const requestId = decodeId(encryptedId);
        if (requestId) {
          const found = requests.find(r => r.id === requestId);
          if (found) setSelectedRequest(found);
        }
      }
    }
  }, [requests]);

  useEffect(() => {
    const res = requests.filter(r => {
      const matchesSearch = r.product?.toLowerCase().includes(search.toLowerCase()) || r.buyer?.toLowerCase().includes(search.toLowerCase());
      const matchesProduct = filters.product === "all" || r.product === filters.product;
      const matchesPayment = filters.payment === "all" || r.paymentMode === filters.payment;
      const matchesDelivery = filters.delivery === "all" || r.deliveryType === filters.delivery;
      return matchesSearch && matchesProduct && matchesPayment && matchesDelivery;
    });
    setFilteredRequests(res);
  }, [search, filters, requests]);

  const handleSendEmail = async () => {
    if (!mailMessage || mailMessage.length < 5) return;
    try {
      const templateParams = {
        header: "AGRIVESTIA MARKETPLACE",
        subject: `Offer for ${selectedRequest.quantity} of ${selectedRequest.product}`,
        message: mailMessage,
        email: selectedRequest.buyer_contact
      };
      await emailjs.send("service_pehhhv8", "template_rql77hn", templateParams, "9o1vflADP-wePeEFd");
      setMailMessage("");
      setIsSent(true);
      setTimeout(() => { setIsSent(false); setSelectedRequest(null); }, 3000);
    } catch (err) { console.error(err); }
  };

  const handleProposal = async () => {
    if (!userId) {
      window.location.href = `/login?redirect=central_market?id=${selectedRequest.id}`;
      return;
    }
    if (!proposalForm.offer || !proposalForm.contact || isSubmitting) return;

    const buyerId = selectedRequest.id.split("_")[0];
    if (userId === buyerId) {
      alert("You cannot bid on your own purchase request.");
      return;
    }

    setIsSubmitting(true);
    try {
      let verifiedName = userName || "Anonymous Seller";

      // Create the proposal/bid
      const proposalRef = await addDoc(collection(db, "market_proposals"), {
        requestId: selectedRequest.id,
        buyerId: buyerId,
        sellerId: userId || "anonymous",
        sellerName: verifiedName,
        sellerContact: proposalForm.contact,
        product: selectedRequest.product,
        quantityOffer: selectedRequest.quantity,
        priceOffer: proposalForm.offer,
        notes: proposalForm.notes,
        status: "pending",
        timestamp: serverTimestamp(),
        isRead: false
      });

      // Get buyer details for notification and email
      const buyerDoc = await getDoc(doc(db, "users", buyerId));
      if (buyerDoc.exists()) {
        const buyerData = buyerDoc.data();
        const buyerEmail = buyerData.email || buyerData.email_address || selectedRequest.buyer_contact;

        // Create notification record
        await addDoc(collection(db, "user_notifications"), {
          userId: buyerId,
          type: "bid_received",
          title: `New Bid from ${verifiedName}`,
          message: `${verifiedName} placed a bid of ₦${proposalForm.offer} for ${selectedRequest.quantity} of ${selectedRequest.product}`,
          bidId: proposalRef.id,
          productName: selectedRequest.product,
          sellerName: verifiedName,
          bidAmount: proposalForm.offer,
          timestamp: serverTimestamp(),
          isRead: false
        });

        // Send email notification
        try {
          const emailParams = {
            to_email: buyerEmail,
            to_name: buyerData.name || "Valued Customer",
            subject: `New Bid Received: ${selectedRequest.product}`,
            product_name: selectedRequest.product,
            seller_name: verifiedName,
            bid_amount: proposalForm.offer,
            quantity: selectedRequest.quantity,
            seller_contact: proposalForm.contact,
            message: `You have received a new bid of ₦${proposalForm.offer} for ${selectedRequest.quantity} of ${selectedRequest.product} from ${verifiedName}. Log in to your Agrivestia dashboard to review and respond to this offer.`,
            dashboard_link: `${window.location.origin}/market_dashboard`,
            year: new Date().getFullYear()
          };

          await emailjs.send("service_pehhhv8", "template_rql77hn", emailParams, "9o1vflADP-wePeEFd");
        } catch (emailError) {
          console.warn("Email notification failed, but bid was created:", emailError);
        }
      }

      setIsSent(true);
      setProposalForm({ offer: "0", contact: "", notes: "" }); // Reset with zero as per previous state patterns if needed, else same
      setTimeout(() => {
        setIsSent(false);
        setSelectedRequest(null);
        const url = new URL(window.location);
        url.searchParams.delete('id');
        window.history.replaceState({}, '', url);
      }, 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to submit proposal. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: "var(--font-sans)", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet>
        <title>Central Market — Buy &amp; Sell Agricultural Commodities | Agrivestia</title>
        <meta name="description" content="Browse bulk agricultural produce requests, place bids, and connect directly with verified buyers and sellers across Africa on Agrivestia's Central Market." />
        <meta name="keywords" content="buy wholesale crops, sell farm produce, agricultural commodity trading, farming marketplace Africa" />
      </Helmet>

      {/* Navbar Style Header */}
      <header style={{ background: "var(--color-white)", borderBottom: "1px solid var(--color-gray-100)", position: "sticky", top: 0, zIndex: 100, padding: "12px clamp(20px, 5vw, 60px)" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }} onClick={() => window.location.href = "/"}>
            <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={18} color="white" />
            </div>
            <span style={{ fontWeight: 800, fontSize: "18px", color: "var(--color-primary-dark)", letterSpacing: "-0.02em" }}>AGRIVESTIA <span style={{ color: "var(--color-gray-400)", fontWeight: 500 }}>MARKET</span></span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
            <a href="/blog" style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-primary)", textDecoration: "none" }}>Health & News (Blog)</a>
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              {userId && userName ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }} title="Logged in as">
                  <div style={{ textAlign: "right" }} className="hide-mobile">
                    <div style={{ fontSize: "11px", color: "var(--color-gray-400)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Welcome back</div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-gray-800)" }}>{userName}</div>
                  </div>
                  <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--gradient-primary)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700 }}>
                    {userName[0]?.toUpperCase()}
                  </div>
                </div>
              ) : (
                <button onClick={() => window.location.href = "/login"} className="btn-primary" style={{ padding: "8px 20px", fontSize: "13px", gap: "6px" }}>
                  <User size={15} /> Sign In
                </button>
              )}
              <button onClick={() => window.history.back()} style={{ display: "flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "var(--color-gray-500)", fontWeight: 600, fontSize: "14px", cursor: "pointer" }}>
                <ArrowLeft size={16} />  Return
              </button>
            </div>
          </div>
        </div>
      </header>

      <main style={{ padding: "40px clamp(20px, 5vw, 60px)", maxWidth: "1200px", margin: "0 auto" }}>

        {/* Market Hero / Summary */}
        <div style={{ marginBottom: "40px" }}>
          <h1 style={{ fontSize: "32px", fontWeight: 900, color: "var(--color-gray-900)", letterSpacing: "-0.02em", marginBottom: "8px" }}>Central Market</h1>
          <h2 style={{ color: "var(--color-gray-500)", fontSize: "16px", fontWeight: 400, marginTop: 0 }}>Browse {requests.length} live bulk agricultural purchase requests and sell farm produce to verified buyers across Africa.</h2>
        </div>

        {/* Search & Filter Bar */}
        <div className="card" style={{ padding: "20px", marginBottom: "40px", display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: "260px", position: "relative" }}>
            <Search size={18} color="var(--color-gray-400)" style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)" }} />
            <input
              className="input-field"
              placeholder="Search by product or buyer..."
              style={{ marginBottom: 0, paddingLeft: "42px" }}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>

            <select className="input-field" style={{ marginBottom: 0, width: "auto" }} value={filters.payment} onChange={e => setFilters({ ...filters, payment: e.target.value })}>
              <option value="all">Payment Mode</option>
              <option value="Agrivestia Escrow">Escrow</option><option value="LPO (Local purchase order)">LPO</option><option value="Cash">Cash</option><option value="Bank transfer">Transfer</option>
            </select>
            <button onClick={() => { setSearch(""); setFilters({ product: "all", payment: "all", delivery: "all" }); }} style={{ background: "none", border: "none", color: "var(--color-primary)", fontWeight: 700, fontSize: "14px", cursor: "pointer" }}>
              Reset Filters
            </button>
          </div>
        </div>

        {/* Results Grid */}
        <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "24px" }}>
          {filteredRequests.map(req => (
            <MarketplaceCard
              key={req.id}
              req={req}
              onClick={(r) => setSelectedRequest(r)}
            />
          ))}
        </div>

        {filteredRequests.length === 0 && (
          <div style={{ textAlign: "center", padding: "100px 20px" }}>
            <Search size={48} color="var(--color-gray-200)" style={{ margin: "0 auto 20px" }} />
            <h3 style={{ fontSize: "18px", color: "var(--color-gray-900)", marginBottom: "8px" }}>No results found</h3>
            <p style={{ color: "var(--color-gray-400)" }}>Try adjusting your search or filters to find what you're looking for.</p>
          </div>
        )}
      </main>

      {/* Details / Contact Modal */}
      {selectedRequest && (
        <div className="overlay"
          onClick={() => {
            setSelectedRequest(null);
            const url = new URL(window.location);
            url.searchParams.delete('id');
            window.history.replaceState({}, '', url);
          }}
          style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 1000 }}
        >
          <div className="card" onClick={e => e.stopPropagation()} style={{ width: "100%", maxWidth: "600px", padding: 0, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", animation: "slideInUp 0.3s ease" }}>
            {/* Modal Header */}
            <div style={{ padding: "32px 32px 24px", background: "var(--color-primary-dark)", color: "white", position: "relative" }}>
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  // Clear URL param to prevent re-opening
                  const url = new URL(window.location);
                  url.searchParams.delete('id');
                  window.history.replaceState({}, '', url);
                }}
                style={{
                  position: "absolute", top: "24px", right: "24px", background: "rgba(255,255,255,0.2)",
                  border: "none", borderRadius: "50%", width: "36px", height: "36px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "white", cursor: "pointer", zIndex: 50, transition: "all 0.2s"
                }}
              >
                <X size={20} />
              </button>
              <div style={{ textTransform: "uppercase", fontSize: "11px", fontWeight: 700, opacity: 0.7, letterSpacing: "0.1em", marginBottom: "8px" }}>Purchase Request</div>
              <h2 style={{ fontSize: "28px", fontWeight: 900, marginBottom: "4px", letterSpacing: "-0.02em" }}>{selectedRequest.quantity} {selectedRequest.product}</h2>
              <div style={{ fontSize: "14px", opacity: 0.8, display: "flex", alignItems: "center", gap: "6px" }}><MapPin size={14} /> Available for {selectedRequest.deliveryType}</div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "32px", overflowY: "auto", flex: 1 }}>
              <div className="grid-mobile-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "32px" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "8px" }}>Budget / Price</div>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--color-primary)" }}>{selectedRequest.price}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "8px" }}>Buyer</div>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--color-gray-900)" }}>{selectedRequest.buyer}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "8px" }}>Payment Terms</div>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-700)" }}>{selectedRequest.paymentMode}<div style={{ fontSize: "12px", color: "var(--color-gray-400)", fontWeight: 400 }}>{selectedRequest.paymentTerm}</div></div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "8px" }}>Fulfillment</div>
                  <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-gray-700)" }}>{selectedRequest.deliveryDays} Days Timeline</div>
                </div>
              </div>

              <div style={{ marginBottom: "32px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-gray-400)", textTransform: "uppercase", marginBottom: "12px" }}>Quality Requirements</div>
                <div style={{ padding: "20px", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", border: "1px solid var(--color-gray-100)", fontSize: "14px", color: "var(--color-gray-700)", lineHeight: 1.6 }}>
                  {selectedRequest.description}
                </div>
              </div>

              {/* Contact Seller Interaction */}
              <div style={{ borderTop: "1px solid var(--color-gray-100)", paddingTop: "32px" }}>
                {!userId ? (
                  <div style={{ textAlign: "center", padding: "24px", background: "var(--color-primary-light)", borderRadius: "var(--radius-md)" }}>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-primary-dark)", marginBottom: "8px" }}>Sign in to Submit a Bid</div>
                    <p style={{ fontSize: "13px", color: "var(--color-primary)", marginBottom: "20px" }}>You must be logged in to Agrivestia to contact buyers and submit offers.</p>
                    <button onClick={() => window.location.href = `/login?redirect=central_market?id=${selectedRequest.id}`} className="btn-primary" style={{ padding: "12px 24px", width: "100%" }}>Log In to Marketplace</button>
                  </div>
                ) : userId === selectedRequest.id.split("_")[0] ? (
                  <div style={{ textAlign: "center", padding: "24px", background: "var(--color-gray-100)", borderRadius: "var(--radius-md)" }}>
                    <AlertTriangle size={24} color="var(--color-gray-400)" style={{ margin: "0 auto 12px" }} />
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "var(--color-gray-700)", marginBottom: "4px" }}>This is Your Request</div>
                    <p style={{ fontSize: "13px", color: "var(--color-gray-500)" }}>You cannot submit proposals to your own marketplace requests.</p>
                  </div>
                ) : isSent ? (
                  <div style={{ textAlign: "center", padding: "20px", background: "var(--color-success-light)", borderRadius: "var(--radius-md)", animation: "fadeInUp 0.3s ease" }}>
                    <CheckCircle2 size={32} color="var(--color-success)" style={{ margin: "0 auto 12px" }} />
                    <h4 style={{ color: "#15803d", fontWeight: 700 }}>Request Sent!</h4>
                    <p style={{ color: "#166534", fontSize: "13px" }}>The buyer was notified. They'll reach out to you directly.</p>
                  </div>
                ) : (
                  <>
                    <h4 style={{ fontSize: "15px", fontWeight: 800, color: "var(--color-gray-900)", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                      Submit your bid to the buyer? <Send size={15} color="var(--color-primary)" />
                    </h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      <input
                        className="input-field"
                        placeholder="Your Price Offer (e.g. ₦150,000)"
                        value={proposalForm.offer}
                        onChange={e => setProposalForm({ ...proposalForm, offer: e.target.value })}
                        style={{ marginBottom: 0 }}
                      />
                      <input
                        className="input-field"
                        placeholder="Your Contact Information (Phone/WhatsApp)"
                        value={proposalForm.contact}
                        onChange={e => setProposalForm({ ...proposalForm, contact: e.target.value })}
                        style={{ marginBottom: 0 }}
                      />
                      <textarea
                        className="input-field"
                        rows={3}
                        placeholder="Additional details / quality specs..."
                        value={proposalForm.notes}
                        onChange={e => setProposalForm({ ...proposalForm, notes: e.target.value })}
                        style={{ marginBottom: 0 }}
                      />
                      <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
                        <button
                          className="btn-primary"
                          onClick={handleProposal}
                          disabled={!proposalForm.offer || !proposalForm.contact || isSubmitting}
                          style={{ flex: 1, justifyContent: "center", padding: "14px", opacity: (!proposalForm.offer || !proposalForm.contact || isSubmitting) ? 0.6 : 1 }}
                        >
                          {isSubmitting ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <div className="spinner" style={{ width: "16px", height: "16px", border: "2px solid white", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                              Submitting...
                            </div>
                          ) : "Submit Proposal"}
                        </button>
                        {selectedRequest.buyer_contact_type === "WhatsApp" && (
                          <ReactWhatsapp
                            number={selectedRequest.buyer_contact}
                            message={`Hi ${selectedRequest.buyer}, I saw your request for ${selectedRequest.quantity} of ${selectedRequest.product} on Agrivestia Market. I have them available.`}
                            style={{ padding: "0 14px", background: "#25D366", color: "white", border: "none", borderRadius: "12px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <MessageCircle size={20} />
                          </ReactWhatsapp>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{ background: "var(--color-primary-dark)", padding: "60px clamp(20px, 5vw, 60px)", color: "white" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "24px" }}>
            <Leaf size={24} color="var(--color-primary)" />
            <span style={{ fontWeight: 800, fontSize: "20px" }}>AGRIVESTIA</span>
          </div>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "14px", marginBottom: "32px" }}>Empowering Agricultural Trade Across the Globe.</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "24px", color: "rgba(255,255,255,0.4)" }}>
            © {new Date().getFullYear()} Agrivestia Marketplace. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}